import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, CircularProgress, Divider, Typography } from '@mui/material';
import {
  MISSION_STATUS_COLOR,
  Mission,
  MissionRouteStatus,
  RosterEntity,
  runMissionMonitor,
  deleteMission,
  getMission,
  listMissions,
  listRosterEntities,
  replaceMissionAssignees,
  replaceMissionGeofences,
  replaceMissionWaypoints,
  activateNamedRoute,
  deleteNamedRoute,
  saveMissionRoute,
  saveMissionRouteIntel,
  saveNamedRoute,
  startMissionRun,
  endMissionRun,
  updateMission,
} from '../../services/missionApi';
import { PacketSubjectOptions, createPacket, getSubjectOptions } from '../../services/intelPacketApi';
import RoutePlannerDialog from '../routePlanner/RoutePlannerDialog';
import { alternatePayload, geometryKey, routeStats } from '../routePlanner/io/missionRoute';
import { isGenericWaypointLabel, sequentialWaypointLabel } from '../routePlanner/io/waypointLabel';
import { RouteProfile } from '../routePlanner/types';
import SubjectDossierPanel from '../subjects/SubjectDossierPanel';
import MissionActionBar from './MissionActionBar';
import MissionAreasTab from './MissionAreasTab';
import MissionBaselinePanel from './MissionBaselinePanel';
import MissionLiveTab from './MissionLiveTab';
import MissionPhotosTab from './MissionPhotosTab';
import MissionDwellsTab from './MissionDwellsTab';
import MissionSignalsTab from './MissionSignalsTab';
import MissionReportTab from './MissionReportTab';
import MissionPosture from './MissionPosture';
import MissionRouteWidget from './MissionRouteWidget';
import { rosterDeviceLabel, watchlistLabels } from './areaPresence';
import MissionSetupWizard from './MissionSetupWizard';
import PickerDialog from './PickerDialog';
import { RouteOptionPreview } from './map/MissionOptionPreviewLayer';
import { useStopSentinelLive } from './useStopSentinelLive';

const GOLD = '#f5b942';

function setupState(mission: Mission, assignedCount: number) {
  const intel = mission.route_intel || {};
  const started = !!(
    mission.route_geometry
    || assignedCount
    || intel.places
    || intel.events
    || intel.weather
    || intel.corridor
  );
  const complete = !!(
    mission.route_geometry
    && assignedCount > 0
    && intel.places
    && intel.events
    && intel.corridor
  );
  return { started, complete };
}

export interface MissionWorkspaceProps {
  /** Scope the list to one packet. Omit for standalone missions. */
  packetId?: number;
  /**
   * Open straight onto one mission and hide the list. Used when the host
   * already has its own combined list of files to come back to.
   */
  missionId?: number | null;
  /** Shown as a back arrow in single-mission mode. */
  onBack?: () => void;
  /** Seed coordinate offered when adding the first waypoint. */
  defaultCenter?: { latitude: number; longitude: number; label?: string } | null;
  onError: (message: string | null) => void;
  /**
   * Hosts with their own map handle route planning inline. Without this the
   * workspace opens its own full-screen planner instead.
   */
  onPlanRoute?: (mission: Mission) => void;
  /** Push the mission's waypoints onto the host map. */
  onWaypointsChange?: (mission: Mission | null) => void;
  /**
   * Bumped by a host that has changed the mission behind this panel's back.
   *
   * Hosts with a reference dock let an operator reroute from there, and the
   * workspace holds its own copy of the mission — without a nudge it goes on
   * reporting the distance of a route the convoy is no longer driving.
   */
  refreshSignal?: number;
  /** Centre the host map, used when an operator clicks a route alert. */
  onFocusPoint?: (longitude: number, latitude: number) => void;
  /**
   * Hosts with a map arm click-to-place while this is true. The workspace
   * writes the commit function onto `waypointPickRef` for the duration.
   */
  onWaypointPickChange?: (active: boolean) => void;
  waypointPickRef?: React.MutableRefObject<((longitude: number, latitude: number) => void) | null>;
  /**
   * Set by hosts that show the run in their own reference dock. Two copies of
   * the live panel would poll the same endpoint twice and disagree about what
   * has been acknowledged.
   */
  hideRunStatus?: boolean;
  /** Live monitor snapshot from a host that already polls (the dock Live tab). */
  liveStatus?: MissionRouteStatus | null;
  onLiveStatus?: (status: MissionRouteStatus) => void;
  onPreviewOption?: (preview: RouteOptionPreview | null) => void;
  /** Host-owned one-click map redirect (lead → tapped destination). */
  onEmergencyReroute?: () => void;
  onCancelEmergencyReroute?: () => void;
  emergencyReroutePicking?: boolean;
  emergencyRerouteBusy?: boolean;
  emergencyRerouteError?: string | null;
  /** Open the host dock's Photos tab. */
  onOpenPhotos?: () => void;
}

const ActionText: React.FC<{
  onClick?: (e: React.MouseEvent<HTMLElement>) => void;
  disabled?: boolean;
  accent?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, accent, children }) => (
  <Typography
    variant="caption"
    onClick={disabled ? undefined : onClick}
    sx={{
      fontWeight: 700,
      cursor: disabled ? 'default' : 'pointer',
      color: disabled ? 'text.disabled' : accent ? GOLD : 'text.primary',
      '&:hover': disabled ? undefined : { textDecoration: 'underline' },
    }}
  >
    {children}
  </Typography>
);

const MissionWorkspace: React.FC<MissionWorkspaceProps> = ({
  packetId,
  missionId,
  onBack,
  defaultCenter,
  onError,
  onPlanRoute,
  onWaypointsChange,
  onFocusPoint,
  onWaypointPickChange: _onWaypointPickChange,
  waypointPickRef: _waypointPickRef,
  hideRunStatus,
  liveStatus,
  onLiveStatus,
  onPreviewOption,
  refreshSignal,
  onEmergencyReroute,
  onCancelEmergencyReroute,
  emergencyReroutePicking,
  emergencyRerouteBusy,
  emergencyRerouteError,
  onOpenPhotos,
}) => {
  const [missions, setMissions] = useState<Mission[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [devices, setDevices] = useState<PacketSubjectOptions['devices']>([]);
  const [entities, setEntities] = useState<RosterEntity[]>([]);
  const [fences, setFences] = useState<PacketSubjectOptions['geofences']>([]);
  const [localStatus, setLocalStatus] = useState<MissionRouteStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [setupId, setSetupId] = useState<number | null>(null);
  const [setupIntent, setSetupIntent] = useState<'setup' | 'edit'>('setup');
  const [plannerFor, setPlannerFor] = useState<Mission | null>(null);
  const [savingRoute, setSavingRoute] = useState(false);
  const [entityPicker, setEntityPicker] = useState(false);
  const [fencePicker, setFencePicker] = useState(false);
  const [watchSettingsOpen, setWatchSettingsOpen] = useState(false);
  const [kitTick, setKitTick] = useState(0);

  const selected = useMemo(
    () => missions.find((m) => m.id === selectedId) || null,
    [missions, selectedId],
  );
  useStopSentinelLive(selected);

  const applyMission = useCallback((next: Mission) => {
    setMissions((rows) => {
      const exists = rows.some((m) => m.id === next.id);
      return exists ? rows.map((m) => (m.id === next.id ? next : m)) : [next, ...rows];
    });
    setSelectedId(next.id);
    setKitTick((n) => n + 1);
    onWaypointsChange?.(next);
  }, [onWaypointsChange]);

  const refresh = useCallback(async (keepId?: number | null) => {
    if (missionId) {
      const one = await getMission(missionId);
      applyMission(one);
      return [one];
    }
    const rows = await listMissions(packetId ? { packet_id: packetId } : { standalone: true });
    setMissions(rows);
    setKitTick((n) => n + 1);
    let picked: Mission | null = null;
    setSelectedId((cur) => {
      const next = keepId !== undefined ? keepId : cur;
      const id = next && rows.some((r) => r.id === next) ? next : rows[0]?.id ?? null;
      picked = rows.find((r) => r.id === id) || null;
      return id;
    });
    onWaypointsChange?.(picked);
    return rows;
  }, [packetId, missionId, applyMission, onWaypointsChange]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    refresh()
      .catch((e: any) => onError(e?.response?.data?.detail || e?.message || 'Failed to load missions'))
      .finally(() => { if (!cancelled) setLoading(false); });
    getSubjectOptions()
      .then((o) => { setDevices(o.devices || []); setFences(o.geofences || []); })
      .catch(() => { setDevices([]); setFences([]); });
    listRosterEntities()
      .then(setEntities)
      .catch(() => setEntities([]));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [packetId, missionId]);

  useEffect(() => {
    onWaypointsChange?.(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  useEffect(() => {
    // Skipped on mount: the load effect above has already fetched, and racing
    // it would only decide the same thing twice.
    if (!refreshSignal) return;
    refresh(selectedId).catch(() => undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshSignal]);

  const acceptStatus = useCallback((status: MissionRouteStatus) => {
    setLocalStatus(status);
    onLiveStatus?.(status);
  }, [onLiveStatus]);

  const deviceLabel = useCallback(
    (deviceId: string) => rosterDeviceLabel(deviceId, selected?.assignees || [], devices),
    [devices, selected],
  );

  // Assignees are stored per device, so the picker's selection is recovered by
  // matching entities back against them. Rows assigned before entities existed
  // carry no `entity_id` and simply do not light up an option, which is honest
  // — the picker cannot claim a grouping the data does not have.
  const assignedEntities = useMemo(() => {
    const ids = new Set((selected?.assignees ?? []).map((a) => a.entity_id).filter(Boolean));
    return entities.filter((e) => ids.has(e.entity_id));
  }, [entities, selected]);

  // The flag is written per device but chosen per entity, so it is recovered
  // the same way the selection is: an entity reads as a fixed site only when
  // every device it put on the mission says so.
  const stationaryEntities = useMemo(() => {
    const byEntity = new Map<string, boolean>();
    (selected?.assignees ?? []).forEach((a) => {
      if (!a.entity_id) return;
      const held = byEntity.get(a.entity_id);
      byEntity.set(a.entity_id, (held ?? true) && !!a.is_stationary);
    });
    return Array.from(byEntity.entries()).filter(([, fixed]) => fixed).map(([id]) => id);
  }, [selected]);

  const observerCount = useMemo(
    () => (selected?.assignees ?? []).filter((a) => a.is_observer).length,
    [selected],
  );

  const setup = selected ? setupState(selected, assignedEntities.length) : null;
  const openSetup = (intent: 'setup' | 'edit' = 'setup') => {
    if (!selected) return;
    setSetupIntent(intent);
    setSetupId(selected.id);
    setCreateOpen(true);
  };

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    onError(null);
    try {
      await fn();
    } catch (e: any) {
      onError(e?.response?.data?.detail || e?.message || 'Mission update failed');
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  const single = !!missionId;

  return (
    <Box>
      {single ? null : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
          <Typography variant="caption" sx={{ fontWeight: 700, flex: 1 }}>
            {packetId ? 'Missions on this packet' : 'Missions'}
          </Typography>
          <ActionText accent onClick={() => { setSetupIntent('setup'); setSetupId(null); setCreateOpen(true); }}>
            New mission
          </ActionText>
        </Box>
      )}

      {!single && missions.length === 0 && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
          {packetId
            ? 'No missions yet. A mission sends phones to collect against this packet.'
            : 'No standalone missions yet. Create one to plan a route and task phones.'}
        </Typography>
      )}

      {!single && missions.map((m) => {
        const isSelected = m.id === selectedId;
        return (
          <Box
            key={m.id}
            onClick={() => setSelectedId(m.id)}
            sx={{
              display: 'flex', alignItems: 'center', gap: 0.75, py: 0.5, px: 0.75,
              borderRadius: 0.75, cursor: 'pointer',
              bgcolor: isSelected ? 'action.selected' : 'transparent',
              '&:hover': { bgcolor: 'action.hover' },
            }}
          >
            <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: MISSION_STATUS_COLOR[m.status], flexShrink: 0 }} />
            <Typography variant="body2" noWrap sx={{ flex: 1, fontWeight: isSelected ? 700 : 400 }}>
              {m.name}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {(m.waypoints || []).length} wp
              {(m.assignees || []).length ? ` · ${m.assignees.length} phone${m.assignees.length === 1 ? '' : 's'}` : ''}
            </Typography>
          </Box>
        );
      })}

      {selected && (
        <>
          {!single && <Divider sx={{ my: 1.25 }} />}
          <MissionActionBar
            name={selected.name}
            status={selected.status}
            setupStarted={!!setup?.started}
            setupComplete={!!setup?.complete}
            canFile={!selected.packet_id}
            busy={busy}
            showBack={single && !!onBack}
            onBack={onBack}
            onStartSetup={() => openSetup('setup')}
            onEdit={() => openSetup('edit')}
            onStatusChange={(s) => run(async () => {
              applyMission(await updateMission(selected.id, { status: s }));
            })}
            onFilePacket={() => run(async () => {
              await createPacket({
                name: selected.name,
                primary_subject_type: 'mission',
                primary_subject_id: String(selected.id),
                primary_label: selected.name,
                start_collect: true,
              });
              await refresh(selected.id);
            })}
            run={selected.current_run || null}
            canStartRun={!!selected.route_geometry && selected.assignees.length > 0}
            onGoLive={() => run(async () => {
              applyMission(await startMissionRun(selected.id, 'live'));
            })}
            onEndRun={() => run(async () => {
              // Baseline and In progress are both recorded parts; ending
              // either files the run so the two can be compared. Abandoning
              // is only for a mission that never really moved.
              await endMissionRun(
                selected.id,
                selected.status === 'in_progress' || selected.status === 'baseline'
                  ? 'complete'
                  : 'abandoned',
              );
              await refresh(selected.id);
            })}
            onRehearse={() => run(async () => {
              applyMission(await startMissionRun(selected.id, 'rehearsal'));
            })}
            onSettings={() => setWatchSettingsOpen(true)}
            onDelete={() => run(async () => {
              await deleteMission(selected.id);
              if (single) onBack?.();
              else await refresh(null);
            })}
          />
          {selected.packet_name && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              Filed under {selected.packet_name}
            </Typography>
          )}
          {selected.objective && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1, whiteSpace: 'pre-wrap' }}>
              {selected.objective}
            </Typography>
          )}

          <MissionPosture
            mission={selected}
            devices={(liveStatus || localStatus)?.devices || []}
            progress={(liveStatus || localStatus)?.progress || null}
            deviceLabel={deviceLabel}
            checking={false}
            onRefresh={() => {
              run(async () => {
                const status = await runMissionMonitor(selected.id);
                acceptStatus(status);
              });
            }}
            onError={onError}
            onMissionChange={() => { refresh(selected.id).catch(() => undefined); }}
            onEdit={() => openSetup('edit')}
            briefPaused={createOpen || !!plannerFor}
            settingsOpen={watchSettingsOpen}
            onSettingsClose={() => setWatchSettingsOpen(false)}
            onEmergencyReroute={onEmergencyReroute}
            onCancelEmergencyReroute={onCancelEmergencyReroute}
            emergencyReroutePicking={emergencyReroutePicking}
            emergencyRerouteBusy={emergencyRerouteBusy}
            emergencyRerouteError={emergencyRerouteError}
          />

          <MissionRouteWidget
            mission={selected}
            timeline={(liveStatus || localStatus)?.timeline}
            progress={(liveStatus || localStatus)?.progress}
            devices={(liveStatus || localStatus)?.devices}
            events={(liveStatus || localStatus)?.events}
            onFocus={onFocusPoint}
          />

          {selected.assignees.length > 0 && !(liveStatus || localStatus)?.devices?.length && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {selected.assignees.length} device{selected.assignees.length === 1 ? '' : 's'}
              {observerCount === 0
                ? ' · none collect RF, so a follower cannot be detected'
                : ` · ${observerCount} collecting RF`}
            </Typography>
          )}

          <SubjectDossierPanel
            missionId={selected.id}
            onError={onError}
            onFocus={onFocusPoint}
            onOpenPhotos={onOpenPhotos}
          />

          <MissionBaselinePanel
            missionId={selected.id}
            inProgress={selected.status === 'in_progress' || selected.status === 'baseline'}
            onError={onError}
            refreshKey={kitTick}
          />

          {!hideRunStatus && (
            <Box sx={{ mt: 1.25 }}>
              <MissionLiveTab
                key={selected.id}
                mission={selected}
                deviceLabel={deviceLabel}
                onError={onError}
                onMissionChange={() => { refresh(selected.id).catch(() => undefined); }}
                onFocus={onFocusPoint}
                onStatus={acceptStatus}
                onPreviewOption={onPreviewOption}
              />
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  Areas
                </Typography>
                <MissionAreasTab
                  mission={selected}
                  deviceLabel={deviceLabel}
                  onFocus={onFocusPoint}
                  watchlist={watchlistLabels((liveStatus || localStatus)?.events)}
                />
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  Photos
                </Typography>
                <MissionPhotosTab
                  missionId={selected.id}
                  missionName={selected.name}
                  refreshKey={(liveStatus || localStatus)?.events?.length}
                  timeline={(liveStatus || localStatus)?.timeline}
                  canAlert={selected.status === 'in_progress' || selected.status === 'baseline'}
                  onFocus={onFocusPoint}
                  onAlerted={setLocalStatus}
                />
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  Dwells
                </Typography>
                <MissionDwellsTab
                  missionId={selected.id}
                  started={!!selected.started_at || selected.status === 'in_progress'}
                  refreshKey={(liveStatus || localStatus)?.events?.length}
                  onFocus={onFocusPoint}
                />
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                  Signals
                </Typography>
                <MissionSignalsTab
                  missionId={selected.id}
                  live={selected.status === 'in_progress' || selected.status === 'baseline'}
                  hasAreas={selected.geofences.length > 0}
                  onFocus={onFocusPoint}
                />
              </Box>
              <Box sx={{ mt: 1.5 }}>
                <MissionReportTab
                  mission={selected}
                  onError={onError}
                  onMissionChange={() => { refresh(selected.id).catch(() => undefined); }}
                  refreshKey={(liveStatus || localStatus)?.events?.length}
                />
              </Box>
            </Box>
          )}
        </>
      )}

      <PickerDialog
        open={entityPicker}
        title="Assign entities"
        subtitle="Everything an entity carries goes with it."
        options={entities.map((e) => ({
          id: e.entity_id,
          primary: e.entity_id,
          secondary: e.devices.length
            ? e.devices.map((d) => d.kind).join(', ')
            : 'no devices registered',
          // Said here rather than after the fact, because it decides whether
          // this movement can detect a follower at all.
          note: e.observer_count === 0 ? 'no RF' : undefined,
          warn: e.observer_count === 0,
        }))}
        selected={assignedEntities.map((e) => e.entity_id)}
        emptyText="No entities on the roster yet."
        saving={busy}
        roleToggle={{
          initial: stationaryEntities,
          offLabel: 'Convoy',
          onLabel: 'Fixed site',
          hint: 'A fixed site collects from one spot. It never trips off-route alerts, '
            + 'never drives route progress, and is ignored when the convoy reroutes.',
        }}
        onClose={() => setEntityPicker(false)}
        onSave={(ids, fixedSites) => {
          if (!selected) return;
          setEntityPicker(false);
          run(async () => {
            await replaceMissionAssignees(selected.id, ids.map((entity_id) => ({
              entity_id,
              stationary: fixedSites.includes(entity_id),
            })));
            await refresh(selected.id);
          });
        }}
      />

      <PickerDialog
        open={fencePicker}
        title="Watch areas"
        subtitle="Arrivals and departures are logged for assigned phones."
        options={fences.map((f) => ({
          id: String(f.id),
          primary: f.name,
          secondary: f.course_id || undefined,
          note: f.active === false ? 'inactive' : undefined,
        }))}
        selected={(selected?.geofences || []).map((g) => String(g.geofence_id))}
        emptyText="No geofences defined yet."
        saving={busy}
        onClose={() => setFencePicker(false)}
        onSave={(ids) => {
          if (!selected) return;
          setFencePicker(false);
          run(async () => {
            await replaceMissionGeofences(selected.id, ids.map((id) => Number(id)));
            await refresh(selected.id);
          });
        }}
      />

      <RoutePlannerDialog
        open={!!plannerFor}
        title={plannerFor?.name || 'Mission'}
        missionId={plannerFor?.id ?? null}
        mission={plannerFor}
        onClose={() => setPlannerFor(null)}
        initialWaypoints={(plannerFor?.waypoints || []).map((w) => ({
          longitude: w.longitude,
          latitude: w.latitude,
          label: isGenericWaypointLabel(w.label) ? undefined : (w.label || undefined),
          notes: w.notes || undefined,
        }))}
        initialProfile={(plannerFor?.route_profile as RouteProfile) || undefined}
        initialPrimaryName={typeof plannerFor?.route_options?.route_name === 'string'
          ? plannerFor.route_options.route_name
          : ''}
        initialAlternateName={plannerFor?.alt_route?.label || ''}
        restoreAlternateKey={plannerFor?.alt_route?.geometry
          ? geometryKey(plannerFor.alt_route.geometry)
          : null}
        geofences={(plannerFor?.geofences || [])
          .filter((f) => (f.geometry?.coordinates?.[0]?.length || 0) > 2)
          .map((f) => ({ name: f.name, coordinates: f.geometry!.coordinates[0] }))}
        center={defaultCenter
          ? { longitude: defaultCenter.longitude, latitude: defaultCenter.latitude }
          : null}
        storedIntel={(plannerFor?.route_intel as any) || null}
        storedIntelStale={plannerFor?.route_intel_stale}
        saveLabel="Save to mission"
        saving={savingRoute}
        onSave={async ({ waypoints, route, alternate, alternateName, profile, options, intel, namedSlot }) => {
          if (!plannerFor) return;
          setSavingRoute(true);
          onError(null);
          try {
            const stops = waypoints.map((w, i) => ({
              label: sequentialWaypointLabel(w.label, i),
              latitude: w.latitude,
              longitude: w.longitude,
              notes: w.notes,
            }));
            const fallback = alternatePayload(alternate, profile, options, alternateName, stops);
            if (!namedSlot || namedSlot.live) {
              await replaceMissionWaypoints(plannerFor.id, stops);
              await saveMissionRoute(plannerFor.id, {
                profile,
                options,
                geometry: route?.geometry || null,
                stats: routeStats(route, profile),
                alternate: fallback,
                clear_alternate: !fallback,
                legs: (intel as { directions?: { legs?: unknown[] } })?.directions?.legs
                  || route?.legs,
              });
              if (Object.keys(intel).length > 0) {
                try {
                  await saveMissionRouteIntel(plannerFor.id, intel as any);
                } catch {
                  // The route is saved; failing to keep the reference copy is not
                  // worth telling the operator their save failed.
                }
              }
            } else {
              await saveNamedRoute(plannerFor.id, {
                id: namedSlot.draft ? undefined : namedSlot.id,
                label: namedSlot.label,
                waypoints: stops,
                profile,
                options,
                geometry: route?.geometry || null,
                stats: routeStats(route, profile),
                alternate: fallback,
                clear_alternate: !fallback,
              });
            }
            const next = await getMission(plannerFor.id);
            setPlannerFor(next);
            await refresh(plannerFor.id);
            return next;
          } catch (e: any) {
            onError(e?.response?.data?.detail || e?.message || 'Could not save the route');
          } finally {
            setSavingRoute(false);
          }
        }}
        onActivateNamedRoute={async (id) => {
          if (!plannerFor) return;
          setSavingRoute(true);
          onError(null);
          try {
            const next = await activateNamedRoute(plannerFor.id, id);
            setPlannerFor(next);
            await refresh(plannerFor.id);
          } catch (e: any) {
            onError(e?.response?.data?.detail || e?.message || 'Could not switch route');
          } finally {
            setSavingRoute(false);
          }
        }}
        onDeleteNamedRoute={async (id) => {
          if (!plannerFor) return;
          setSavingRoute(true);
          onError(null);
          try {
            const next = await deleteNamedRoute(plannerFor.id, id);
            setPlannerFor(next);
            await refresh(plannerFor.id);
          } catch (e: any) {
            onError(e?.response?.data?.detail || e?.message || 'Could not remove that route');
          } finally {
            setSavingRoute(false);
          }
        }}
      />

      <MissionSetupWizard
        open={createOpen}
        intent={setupIntent}
        hidden={!!plannerFor || entityPicker || fencePicker}
        packetId={packetId}
        mission={setupId ? (missions.find((m) => m.id === setupId) || null) : null}
        assignedCount={setupId ? assignedEntities.length : 0}
        watchedCount={setupId ? (missions.find((m) => m.id === setupId)?.geofences.length || 0) : 0}
        busy={busy}
        onClose={() => {
          setCreateOpen(false);
          if (setupId) refresh(setupId).catch(() => undefined);
        }}
        onError={onError}
        onCreated={async (id) => { setSetupId(id); await refresh(id); }}
        onPlanRoute={(m) => setPlannerFor(m)}
        onAssign={() => setEntityPicker(true)}
        onWatchAreas={() => setFencePicker(true)}
        onFinished={(next) => {
          setCreateOpen(false);
          if (next) applyMission(next);
          else if (setupId) refresh(setupId).catch(() => undefined);
        }}
        onBaselineChanged={() => setKitTick((n) => n + 1)}
        onIntelSaved={(id) => { refresh(id).catch(() => undefined); }}
      />
    </Box>
  );
};

export default MissionWorkspace;
